import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import withSessionCheck from '../../../withSessionCheck';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Fade, CircularProgress, Tooltip
} from '@mui/material';
import jsPDF from 'jspdf';

const PharmacyIndex = () => {
  const history = useHistory();
  const { isAuthenticated, companyId, token, logout } = useAuth();
  
  const getCurrentDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  const [doctorEntries, setDoctorEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [mounted, setMounted] = useState(false);
  const [banner, setBanner] = useState({ show: false, message: '', type: '' });
  const [fromDate, setFromDate] = useState(getCurrentDate());
  const [toDate, setToDate] = useState(getCurrentDate());
  const [viewMode, setViewMode] = useState('dispensing'); // 'dispensing' or 'billed'
  const [billedInvoices, setBilledInvoices] = useState([]);
  const [filteredBilledInvoices, setFilteredBilledInvoices] = useState([]);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
    if (viewMode === 'dispensing') {
      fetchDoctorEntries();
    } else {
      fetchBilledInvoices();
    }
  }, [isAuthenticated, companyId, token, logout, viewMode]);

  useEffect(() => {
    if (viewMode === 'dispensing') {
      const filtered = doctorEntries.filter(entry =>
        entry.PatientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.DoctorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.MRDNo?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredEntries(filtered);
    } else {
      const filtered = billedInvoices.filter(invoice =>
        invoice.PatientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.DoctorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.InvoiceNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.MRDNo?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredBilledInvoices(filtered);
    }
  }, [searchQuery, doctorEntries, billedInvoices, viewMode]);

  const showBanner = (message, type = 'info') => {
    setBanner({ show: true, message, type });
    setTimeout(() => {
      setBanner({ show: false, message: '', type: '' });
    }, 4000);
  };

  const fetchDoctorEntries = async () => {
    setLoading(true);
    try {
      let endpoint = `/${companyId}`;
      if (fromDate && toDate) {
        endpoint = `/${companyId}/${fromDate}/${toDate}`;
      }
      const response = await fetchData('PharmacyApi', endpoint, token);
      if (response.responseCode === '1111') {
        setDoctorEntries(response.response_data || []);
        setFilteredEntries(response.response_data || []);
      }
    } catch (error) {
      console.error('Error fetching doctor entries:', error);
      showBanner('Failed to load doctor entries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBilledInvoices = async () => {
    setLoading(true);
    try {
      let endpoint = `/${companyId}`;
      if (fromDate && toDate) {
        endpoint = `/${companyId}/${fromDate}/${toDate}`;
      }
      const response = await fetchData('MedicineDispensingApi', endpoint, token);
      if (response.responseCode === '1111') {
        setBilledInvoices(response.response_data || []);
        setFilteredBilledInvoices(response.response_data || []);
      }
    } catch (error) {
      console.error('Error fetching billed invoices:', error);
      showBanner('Failed to load billed invoices', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    showBanner('Refreshing data...', 'info');
    if (viewMode === 'dispensing') {
      fetchDoctorEntries();
    } else {
      fetchBilledInvoices();
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setSearchQuery(''); // Clear search when switching modes
  };

  const handleDispense = (id) => {
    history.push(`/PharmacyDispense/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getPaymentType = (paymentTypeId) => {
    switch (paymentTypeId) {
      case 1:
        return 'Cash';
      case 2:
        return 'UPI';
      case 3:
        return 'Card';
      default:
        return 'Unknown';
    }
  };

  const getPaymentTypeColor = (paymentTypeId) => {
    switch (paymentTypeId) {
      case 1: // Cash
        return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      case 2: // UPI
        return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
      case 3: // Card
        return 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
      default:
        return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      showBanner('Fetching invoice data for printing...', 'info');
      
      // First, get header data from MedicineDispensing API
      const headerEndpoint = `/${invoice.Id}`;
      const headerResponse = await fetchData('MedicineDispensingApi', headerEndpoint, token);
      
      if (headerResponse.responseCode !== '1111') {
        showBanner('Failed to fetch invoice header data', 'error');
        return;
      }
      
      const headerData = headerResponse.response_data.MedicineDispensing;
      const medicineDetails = headerResponse.response_data.MedicineDispensingDetails || [];
      
      // Generate PDF for printing
      showBanner('Preparing document for printing...', 'info');
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;

      // Header with Company Information
      doc.setFillColor(16, 185, 129);
      doc.rect(0, 0, pageWidth, 51, 'F');
      doc.setTextColor(255, 255, 255);
      
      // Company Information at the top
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text(`${(headerData.CompanyName || 'N/A').toUpperCase()}`, pageWidth / 2, 12, { align: 'center' });
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      doc.text(`${headerData.CompanyAddress || 'N/A'}`, pageWidth / 2, 20, { align: 'center' });
      doc.text(`Phone: ${headerData.CompanyPhone || 'N/A'}`, pageWidth / 2, 26, { align: 'center' });
      
      // Pharmacy Bill Receipt Title
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      doc.text('Medicine Dispensing Invoice', pageWidth / 2, 40, { align: 'center' });
      doc.setFontSize(9);
      doc.text(`Invoice No: ${headerData.BillNo || 'N/A'}`, pageWidth / 2, 46, { align: 'center' });

      yPosition = 61;

      // Invoice Information Section
      doc.setFillColor(248, 250, 252);
      doc.rect(10, yPosition, pageWidth - 20, 45, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.rect(10, yPosition, pageWidth - 20, 45, 'S');

      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');

      // Left column
      doc.text('PATIENT NAME', 15, yPosition + 8);
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(headerData.PatientName || 'N/A', 15, yPosition + 15);

      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('MRD NO', 15, yPosition + 25);
      doc.setTextColor(16, 185, 129);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(headerData.MRDNo || 'N/A', 15, yPosition + 32);

      // Middle column
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('DOCTOR', 80, yPosition + 8);
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(headerData.DoctorName || 'N/A', 80, yPosition + 15);

      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('BILL DATE', 80, yPosition + 25);
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(formatDate(headerData.BillDate), 80, yPosition + 32);

      // Right column
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('PAYMENT TYPE', 145, yPosition + 8);
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(headerData.PaymentTypeName, 145, yPosition + 15);

      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('TOTAL AMOUNT', 145, yPosition + 25);
      doc.setTextColor(220, 38, 38);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`INR ${parseFloat(headerData.BillAmount || 0).toFixed(2)}`, 145, yPosition + 32);

      yPosition += 55;

      // Medicine Details Section
      if (medicineDetails && medicineDetails.length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 100) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFillColor(254, 243, 199);
        doc.rect(10, yPosition, pageWidth - 20, 12, 'F');
        doc.setTextColor(146, 64, 14);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('DISPENSED MEDICINES', 15, yPosition + 8);

        yPosition += 18;

        // Table Header
        doc.setFillColor(30, 41, 59);
        doc.rect(10, yPosition, pageWidth - 20, 12, 'F');
        doc.setTextColor(241, 245, 249);
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('MEDICINE NAME', 15, yPosition + 8);
        doc.text('QTY', 105, yPosition + 8, { align: 'right' });
        doc.text('UNIT PRICE', 140, yPosition + 8, { align: 'right' });
        doc.text('TOTAL', 185, yPosition + 8, { align: 'right' });

        yPosition += 12;
        let grandTotal = 0;

        // Table Rows
        medicineDetails.forEach((medicine, index) => {
          // Check if we need a new page
          if (yPosition > pageHeight - 25) {
            doc.addPage();
            yPosition = 20;
          }

          // Alternate row colors
          if (index % 2 === 0) {
            doc.setFillColor(255, 255, 255);
          } else {
            doc.setFillColor(248, 250, 252);
          }
          doc.rect(10, yPosition, pageWidth - 20, 14, 'F');

          doc.setTextColor(15, 23, 42);
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          
          // Medicine name with text wrapping
          const medicineName = medicine.MedicineName || 'N/A';
          const medicineLines = doc.splitTextToSize(medicineName, 70);
          doc.text(medicineLines, 15, yPosition + 8);

          doc.setTextColor(71, 85, 105);
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          doc.text((medicine.Quantity || 0).toString(), 105, yPosition + 8, { align: 'right' });
          doc.text(`${parseFloat(medicine.Rate || 0).toFixed(2)}`, 140, yPosition + 8, { align: 'right' });
          
          const itemTotal = (medicine.Quantity || 0) * (medicine.Rate || 0);
          grandTotal += itemTotal;
          doc.setTextColor(16, 185, 129);
          doc.setFont(undefined, 'bold');
          doc.text(`${itemTotal.toFixed(2)}`, 185, yPosition + 8, { align: 'right' });

          yPosition += Math.max(14, medicineLines.length * 5 + 4);
        });

        // Total Section
        yPosition += 5;
        doc.setDrawColor(226, 232, 240);
        doc.line(10, yPosition, pageWidth - 10, yPosition);
        yPosition += 8;

        doc.setFillColor(16, 185, 129);
        doc.rect(120, yPosition, pageWidth - 130, 15, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('GRAND TOTAL:', 125, yPosition + 10);
        doc.setFontSize(14);
        doc.text(`${grandTotal.toFixed(2)}`, pageWidth - 15, yPosition + 10, { align: 'right' });

        yPosition += 20;
      }

      // Additional Information
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
      }

      yPosition += 10;
      doc.setFillColor(240, 249, 255);
      doc.rect(10, yPosition, pageWidth - 20, 25, 'F');
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('IMPORTANT NOTES:', 15, yPosition + 8);
      doc.setTextColor(55, 65, 81);
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text('• Please keep this receipt for your records', 15, yPosition + 15);
      doc.text('• Contact pharmacy for any medicine-related queries', 15, yPosition + 20);

      // Footer
      const footerY = pageHeight - 20;
      doc.setDrawColor(226, 232, 240);
      doc.line(10, footerY - 8, pageWidth - 10, footerY - 8);
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 15, footerY - 3);
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, pageWidth - 15, footerY - 3, { align: 'right' });

      // Print the PDF
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Open print dialog
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          // Clean up the URL after printing
          setTimeout(() => {
            URL.revokeObjectURL(pdfUrl);
          }, 1000);
        };
      } else {
        // Fallback: download if popup is blocked
        const fileName = `Pharmacy_Bill_${headerData.BillNo || 'Invoice'}_${headerData.PatientName || 'Patient'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        showBanner('Print dialog blocked. PDF downloaded instead! 📥', 'info');
        return;
      }
      
      showBanner('Pharmacy bill sent to printer! 🖨️', 'success');
      
    } catch (error) {
      console.error('Error printing PDF:', error);
      showBanner('Error preparing document for printing. Please check your connection.', 'error');
    }
  };

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ padding: '10px', maxWidth: '100%' }}>
        {/* Banner Notification */}
        {banner.show && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            minWidth: '300px',
            maxWidth: '500px',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideInRight 0.4s ease-out',
            background: banner.type === 'success'
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : banner.type === 'error'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white'
          }}>
            <span style={{ fontSize: '24px' }}>
              {banner.type === 'success' ? '✅' : banner.type === 'error' ? '❌' : 'ℹ️'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                {banner.type === 'success' ? 'Success' : banner.type === 'error' ? 'Error' : 'Info'}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.95 }}>{banner.message}</div>
            </div>
            <button
              onClick={() => setBanner({ show: false, message: '', type: '' })}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                cursor: 'pointer',
                padding: '4px 8px',
                fontSize: '18px',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
        )}

        <style>{`
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(100px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .table-row-hover {
            transition: all 0.3s ease;
          }
          .table-row-hover:hover {
            background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%) !important;
            box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
          }
        `}</style>

        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          marginTop: '-30px'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            padding: '8px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>💊</span>
              <div>
                <h1 style={{ fontSize: '15px', fontWeight: '700', color: 'white', margin: 0, lineHeight: '1.2' }}>
                  Pharmacy - {viewMode === 'dispensing' ? 'Medicine Dispensing' : 'Billed Invoice Details'}
                </h1>
                <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)', margin: '2px 0 0 0', lineHeight: '1.3' }}>
                  {viewMode === 'dispensing' 
                    ? 'Select doctor entries to dispense medicines and collect payment'
                    : 'View detailed information about billed invoices with date filters'
                  }
                </p>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* View Mode Toggle */}
              <div style={{ display: 'flex', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '2px' }}>
                <button
                  onClick={() => handleViewModeChange('dispensing')}
                  style={{
                    padding: '4px 12px',
                    background: viewMode === 'dispensing' 
                      ? 'rgba(255,255,255,0.95)' 
                      : 'transparent',
                    color: viewMode === 'dispensing' ? '#10b981' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  💊 Dispensing
                </button>
                <button
                  onClick={() => handleViewModeChange('billed')}
                  style={{
                    padding: '4px 12px',
                    background: viewMode === 'billed' 
                      ? 'rgba(255,255,255,0.95)' 
                      : 'transparent',
                    color: viewMode === 'billed' ? '#10b981' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  📄 Billed Invoice
                </button>
              </div>

              {/* Search Bar */}
              <input
                type="text"
                placeholder={viewMode === 'dispensing' ? "🔍 Search patients, doctors, MRD..." : "🔍 Search invoices, patients, doctors..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '5px 10px',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  outline: 'none',
                  background: 'rgba(255,255,255,0.95)',
                  color: '#374151',
                  fontWeight: '500',
                  height: '28px',
                  width: '200px',
                  transition: 'all 0.2s ease'
                }}
              />

              {/* Date Filters */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    if (e.target.value && toDate) {
                      setTimeout(() => {
                        if (new Date(e.target.value) <= new Date(toDate)) {
                          if (viewMode === 'dispensing') {
                            fetchDoctorEntries();
                          } else {
                            fetchBilledInvoices();
                          }
                        }
                      }, 100);
                    }
                  }}
                  style={{
                    padding: '5px 8px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    outline: 'none',
                    background: 'rgba(255,255,255,0.95)',
                    color: '#374151',
                    fontWeight: '600',
                    height: '28px',
                    width: '130px'
                  }}
                />
                <span style={{ color: 'white', fontSize: '12px', fontWeight: '600' }}>to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    if (fromDate && e.target.value) {
                      setTimeout(() => {
                        if (new Date(fromDate) <= new Date(e.target.value)) {
                          if (viewMode === 'dispensing') {
                            fetchDoctorEntries();
                          } else {
                            fetchBilledInvoices();
                          }
                        }
                      }, 100);
                    }
                  }}
                  style={{
                    padding: '5px 8px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    outline: 'none',
                    background: 'rgba(255,255,255,0.95)',
                    color: '#374151',
                    fontWeight: '600',
                    height: '28px',
                    width: '130px'
                  }}
                />
              </div>

              {/* Refresh Button */}
              <Tooltip title="Refresh data" arrow>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  style={{
                    padding: '0 12px',
                    background: loading 
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(255,255,255,0.95)',
                    color: loading ? 'rgba(255,255,255,0.7)' : '#10b981',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.3s ease',
                    height: '28px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span style={{ fontSize: '12px' }}>🔄</span>
                  Refresh
                </button>
              </Tooltip>
            </div> 
          </div>

          {/* Table */}
          <div style={{ padding: '12px 16px', minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: '400px',
                flex: 1
              }}>
                <CircularProgress style={{ color: '#10b981', width: '48px', height: '48px' }} />
                <p style={{ marginTop: '20px', color: '#6b7280', fontSize: '14px', fontWeight: '600' }}>
                  Loading {viewMode === 'dispensing' ? 'doctor entries' : 'billed invoices'}...
                </p>
              </div>
            ) : (viewMode === 'dispensing' ? filteredEntries : filteredBilledInvoices).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>
                  {viewMode === 'dispensing' ? '💊' : '📄'}
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
                  {viewMode === 'dispensing' ? 'No Doctor Entries Found' : 'No Billed Invoices Found'}
                </h3>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                  {searchQuery 
                    ? 'Try adjusting your search criteria' 
                    : viewMode === 'dispensing' 
                      ? 'No doctor entries available for dispensing'
                      : 'No billed invoices available for the selected date range'
                  }
                </p>
              </div>
            ) : (
              <TableContainer component={Paper} style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}>
                <Table>
                  <TableHead>
                    <TableRow style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
                      {viewMode === 'dispensing' ? (
                        <>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Entry Date</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Patient Info</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Doctor</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Diagnosis</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invoice No</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invoice Date</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Patient Info</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Doctor</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Amount</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Type</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewMode === 'dispensing' ? (
                      filteredEntries.map((entry, index) => (
                        <TableRow
                          key={entry.Id}
                          className="table-row-hover"
                          style={{
                            background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                            animation: 'fadeIn 0.5s ease-out',
                            animationDelay: `${index * 0.05}s`,
                            animationFillMode: 'both'
                          }}
                        >
                          <TableCell style={{ padding: '6px 10px', fontSize: '11px', fontWeight: '600', color: '#10b981' }}>
                            {formatDate(entry.DoctorEntryDate)}
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>
                              {entry.PatientName || 'N/A'}
                            </div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>
                              MRD: {entry.MRDNo || 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px', fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                            {entry.DoctorName || 'N/A'}
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px', fontSize: '11px', color: '#374151', maxWidth: '300px' }}>
                            {entry.Diagnosis ? (
                              entry.Diagnosis.length > 60 
                                ? entry.Diagnosis.substring(0, 60) + '...'
                                : entry.Diagnosis
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px', textAlign: 'center' }}>
                            <Chip 
                              label={entry.PharmacyStatus || 'Pending'}
                              size="small"
                              style={{
                                background: entry.PharmacyStatus === 'Dispensed' 
                                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                  : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '10px'
                              }}
                            />
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px', textAlign: 'center' }}>
                            <Tooltip title="Dispense Medicine" arrow>
                              <button
                                onClick={() => handleDispense(entry.Id)}
                                disabled={entry.PharmacyStatus === 'Dispensed'}
                                style={{
                                  background: entry.PharmacyStatus === 'Dispensed'
                                    ? 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'
                                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '4px 12px',
                                  cursor: entry.PharmacyStatus === 'Dispensed' ? 'not-allowed' : 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: 'white',
                                  transition: 'all 0.3s ease',
                                  height: '26px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  opacity: entry.PharmacyStatus === 'Dispensed' ? 0.6 : 1
                                }}
                                onMouseEnter={(e) => {
                                  if (entry.PharmacyStatus !== 'Dispensed') {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                💊 {entry.PharmacyStatus === 'Dispensed' ? 'Dispensed' : 'Dispense'}
                              </button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      filteredBilledInvoices.map((invoice, index) => (
                        <TableRow
                          key={invoice.Id || index}
                          className="table-row-hover"
                          style={{
                            background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                            animation: 'fadeIn 0.5s ease-out',
                            animationDelay: `${index * 0.05}s`,
                            animationFillMode: 'both'
                          }}
                        >
                          <TableCell style={{ padding: '6px 10px', fontSize: '11px', fontWeight: '600', color: '#10b981' }}>
                            {invoice.BillNo || 'N/A'}
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px', fontSize: '11px', fontWeight: '600', color: '#10b981' }}>
                            {formatDate(invoice.BillDate)}
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>
                              {invoice.PatientName || 'N/A'}
                            </div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>
                              MRD: {invoice.MRDNo || 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px', fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                            {invoice.DoctorName || 'N/A'}
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#059669' }}>
                            ₹{invoice.BillAmount ? parseFloat(invoice.BillAmount).toFixed(2) : '0.00'}
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px', textAlign: 'center' }}>
                            <Chip 
                              label={getPaymentType(invoice.PaymentTypeId)}
                              size="small"
                              style={{
                                background: getPaymentTypeColor(invoice.PaymentTypeId),
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '10px'
                              }}
                            />
                          </TableCell>
                          <TableCell style={{ padding: '6px 10px', textAlign: 'center' }}>
                            <Tooltip title="Print Comprehensive Bill (includes medicine details, patient info, doctor details & clinic header)" arrow>
                              <button
                                onClick={() => handleDownloadPDF(invoice)}
                                style={{
                                  background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '4px 12px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: 'white',
                                  transition: 'all 0.3s ease',
                                  height: '26px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(124, 58, 237, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                🖨️ Print Bill
                              </button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </div>
      </div>
    </Fade>
  );
};

export default withSessionCheck(PharmacyIndex);
